import React from 'react';
import { Button, Icon, Item, List } from 'semantic-ui-react';
import moment from 'moment';
import {
  EEhsStatus,
  EPtwStatus,
  EUserGroup,
  IBuilding,
  IEhs,
  IProject,
  IPtw,
  IPtwFile,
  IUser,
  IWorkstation,
  ProjectUtils,
} from '@relab/alstom-ptw-shared';

import { EhsCreateModal } from '../ehs/EhsCreateModal';
import { FileDownloadLink } from '../FileDownloadLink';
import { PtwDateEditModal } from './PtwDateEditModal';

import 'moment/locale/it';
import { ptwUpdateAction } from '../../redux/actions';
import { client } from '../../redux/store';
import { TranslationComponent } from '../TranslationComponent';

moment.locale('it');

interface PtwDetailInfosComponentProps {
  user: IUser;
  project: IProject;
  buildings: IBuilding[];
  workstations: IWorkstation[];
  ptw: IPtw;
  ptwUpdateAction: typeof ptwUpdateAction;
  onPtwUpdateRequest: () => void;
}

export const PtwDetailInfosComponent = ({
  user,
  project,
  buildings,
  workstations,
  ptw,
  ptwUpdateAction,
  onPtwUpdateRequest,
}: PtwDetailInfosComponentProps) => {
  const {
    descriptionFile = null,
    ehsFile = null,
    testingSequence = null,
    deletionAttachment = null,
  } = ptw.files ? (ptw.files as unknown as Record<string, IPtwFile>) : {};

  const handleEhsChange = (ehs: IEhs) => {
    ptwUpdateAction({ ...ptw, ehs });
    if (ehs.status === EEhsStatus.CHIUSURA_Y) {
      onPtwUpdateRequest();
    }
  };

  const handleDownload = (ehsId: string) => {
    client.get(`/ptw/${ehsId}/report`).then((response) => {
      const byteCharacters = atob(response.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    });
  };

  return (
    <React.Fragment>
      <Item.Group>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Valid from'} />
            </Item.Header>
            <Item.Description>
              <p>{moment(ptw.startTimestamp).format('LLL')}</p>
            </Item.Description>
          </Item.Content>
        </Item>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Valid until'} />
            </Item.Header>
            <Item.Description>
              <p>
                {moment(ptw.endTimestamp).format('LLL')}
                {ProjectUtils.checkUserPermissionsOnProject(project, user, [
                  EUserGroup.PRTCM,
                  EUserGroup.PRTCM_DELEGATE,
                ]) &&
                  ptw.status === EPtwStatus.ACCETTAZIONE_Y && <PtwDateEditModal projectId={project._id} ptw={ptw} />}
              </p>
            </Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
      <Item.Group>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Train'} />
            </Item.Header>
            <Item.Description>
              <p>{project.trains.find((train) => train._id === ptw.train).name}</p>
            </Item.Description>
          </Item.Content>
        </Item>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Vehicles'} />
            </Item.Header>
            <Item.Description>
              {project.trains
                .find((train) => train._id === ptw.train)
                .vehicles.filter((vehicle) => ptw.vehicles.includes(vehicle._id))
                .map((vehicle) => (
                  <div key={vehicle._id}>{vehicle.name}</div>
                ))}
            </Item.Description>
          </Item.Content>
        </Item>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Building'} />
            </Item.Header>
            <Item.Description>
              <p>{buildings.find((building) => building._id === ptw.building)?.name}</p>
            </Item.Description>
          </Item.Content>
        </Item>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Workstation'} />
            </Item.Header>
            <Item.Description>
              <p>{workstations.find((workstation) => workstation._id === ptw.workstation).name}</p>
            </Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
      <Item.Group>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'People'} />
            </Item.Header>
            <Item.Description>
              <p>{ptw.employees}</p>
            </Item.Description>
          </Item.Content>
        </Item>
        <Item>
          <Item.Content>
            <Item.Header>
              <TranslationComponent stringKey={'Description'} />
            </Item.Header>
            <Item.Description>
              <p>{ptw.descriptionOfOperations}</p>
            </Item.Description>
          </Item.Content>
        </Item>
        {descriptionFile && (
          <Item>
            <Item.Content>
              <Item.Header>Attachment</Item.Header>
              <Item.Description>
                <FileDownloadLink path={`/ptw/${ptw._id}/file/descriptionFile`}>Open</FileDownloadLink>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ehsFile && (
          <Item>
            <Item.Content>
              <Item.Header>EHS</Item.Header>
              <Item.Description>
                <FileDownloadLink path={`/ptw/${ptw._id}/file/ehsFile`}>Open</FileDownloadLink>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.ehs && <EhsCreateModal buildings={buildings} ehs={ptw.ehs} onEhs={handleEhsChange} />}
        {ptw.pc9 && (
          <Item>
            <Item.Content>
              <Item.Header>PC9</Item.Header>
              <Item.Description>
                <p>{ptw.pc9}</p>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.prtcmNotes && ptw.prtcmNotes !== '' && (
          <Item>
            <Item.Content>
              <Item.Header>PrTCM notes</Item.Header>
              <Item.Description>
                <p>{ptw.prtcmNotes}</p>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.extraWorkNotes && ptw.extraWorkNotes !== '' && (
          <Item>
            <Item.Content>
              <Item.Header>Extra work</Item.Header>
              <Item.Description>
                <p>{ptw.extraWorkNotes}</p>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.testingNotes && ptw.testingNotes !== '' && (
          <>
            <Item>
              <Item.Content>
                <Item.Header>Testing notes</Item.Header>
                <Item.Description>
                  <p>{ptw.testingNotes}</p>
                </Item.Description>
              </Item.Content>
            </Item>
            {testingSequence && (
              <Item>
                <Item.Content>
                  <Item.Header>Testing notes file</Item.Header>
                  <Item.Description>
                    <FileDownloadLink path={`/ptw/${ptw._id}/file/testingSequence`}>Open</FileDownloadLink>
                  </Item.Description>
                </Item.Content>
              </Item>
            )}
          </>
        )}
        {typeof ptw.testPassed !== 'undefined' && (
          <Item>
            <Item.Content>
              <Item.Header>Testing result</Item.Header>
              <Item.Description>
                <p>{ptw.testPassed ? 'Test passed' : 'Test not passed'}</p>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.qualityNotes && ptw.qualityNotes !== '' && (
          <Item>
            <Item.Content>
              <Item.Header>Quality notes</Item.Header>
              <Item.Description>
                <p>{ptw.qualityNotes}</p>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.testPassed && (
          <Item>
            <Item.Content>
              <Item.Header>Test report</Item.Header>
              <Item.Description>
                <Button
                  icon
                  onClick={(event: any) => {
                    event.stopPropagation();
                    handleDownload(ptw._id);
                  }}
                >
                  <Icon name="download" />
                </Button>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.requiredTesting && (
          <Item>
            <Item.Content>
              <Item.Header>Re-Testing required</Item.Header>
              <Item.Description>
                {ptw.testingAssignee ? (
                  <List bulleted>
                    <List.Header>Testing Assignee:</List.Header>
                    <List.Item>{ptw.testingAssignee?.name}</List.Item>
                  </List>
                ) : (
                  <p>No testing assignee</p>
                )}
              </Item.Description>
            </Item.Content>
          </Item>
        )}
        {ptw.deletionNotes && (
          <Item>
            <Item.Content>
              <Item.Header>Deletion notes</Item.Header>
              <Item.Description>{ptw.deletionNotes}</Item.Description>
            </Item.Content>
          </Item>
        )}
        {deletionAttachment && (
          <Item>
            <Item.Content>
              <Item.Header>Deletion attachment</Item.Header>
              <Item.Description>
                <FileDownloadLink path={`/ptw/${ptw._id}/file/deletionAttachment`}>Open</FileDownloadLink>
              </Item.Description>
            </Item.Content>
          </Item>
        )}
      </Item.Group>
    </React.Fragment>
  );
};
